import { useMemo } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { IssueItem } from "@/hooks/queries/use-issues";
import type { Status } from "@/lib/api/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Circle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getInitials } from "@/lib/get-initials";
import type { Feature } from "@/lib/api/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePrefersReducedMotion } from "@/hooks/use-reduced-motion";

const categoryOrder: Status["category"][] = ["backlog", "unstarted", "started", "completed", "canceled"];

const categoryMeta: Record<Status["category"], { icon: ReactNode; label: string }> = {
  backlog: { icon: <Circle className="h-3 w-3 text-zinc-500" />, label: "Backlog" },
  unstarted: { icon: <Circle className="h-3 w-3 text-blue-400" />, label: "To Do" },
  started: { icon: <Circle className="h-3 w-3 text-amber-400 fill-amber-400/30" />, label: "In Progress" },
  completed: { icon: <CheckCircle2 className="h-3 w-3 text-emerald-400" />, label: "Done" },
  canceled: { icon: <Circle className="h-3 w-3 text-red-400" />, label: "Canceled" },
};

interface KanbanBoardProps {
  issues: IssueItem[];
  onItemSelect?: (item: IssueItem) => void;
  selectedItemId?: string | undefined;
}

interface KanbanColumn {
  category: Status["category"];
  statusName: string;
  items: IssueItem[];
}

export function KanbanBoard({ issues, onItemSelect, selectedItemId }: KanbanBoardProps) {
  const isMobile = useIsMobile();
  const prefersReduced = usePrefersReducedMotion();
  const columns = useMemo(() => {
    // Only show features on the board (tasks are sub-items)
    const features = issues.filter((i) => i.type === "feature");
    
    // Group by status category
    const grouped = new Map<Status["category"], KanbanColumn>();
    
    for (const cat of categoryOrder) {
      grouped.set(cat, { category: cat, statusName: categoryMeta[cat].label, items: [] });
    }

    for (const item of features) {
      const cat = item.data.status?.category ?? "backlog";
      const col = grouped.get(cat);
      if (col) col.items.push(item);
    }

    return Array.from(grouped.values());
  }, [issues]);

  const hasAnyItems = columns.some((col) => col.items.length > 0);

  if (!hasAnyItems) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-muted-foreground">No issues found. Create your first feature to get started.</p>
      </div>
    );
  }

  return (
    <div className={cn("gap-3 h-full p-4", isMobile ? "flex flex-col space-y-4" : "grid grid-cols-5")}>
      {columns.map((col) => (
        <div key={col.category} className="flex flex-col min-w-0">
          {/* Column header */}
          <div className="flex items-center gap-2 px-2 py-2 mb-2">
            {categoryMeta[col.category].icon}
            <span className="text-xs font-medium text-muted-foreground">
              {categoryMeta[col.category].label}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums ml-auto">
              {col.items.length}
            </span>
          </div>

          {/* Cards */}
          <div className="flex-1 space-y-2 overflow-y-auto">
            <AnimatePresence initial={false}>
              {col.items.map((item) => {
                const feature = item.data as Feature;
                const isSelected = item.data.id === selectedItemId;
                const taskTotal = feature._count?.tasks ?? 0;
                const taskDone = feature.completedTaskCount ?? 0;
                const progress = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

                const cardContent = (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="text-xs text-muted-foreground font-mono">
                          {feature.identifier}
                        </span>
                        <p className="text-sm font-medium mt-0.5 line-clamp-2">
                          {feature.title}
                        </p>
                      </div>
                    </div>

                    {/* Footer: assignee + task progress */}
                    <div className="flex items-center justify-between mt-3">
                      {feature.assignee ? (
                        <Avatar className="h-5 w-5">
                          {feature.assignee.avatarUrl && (
                            <AvatarImage src={feature.assignee.avatarUrl} alt={feature.assignee.name} />
                          )}
                          <AvatarFallback className="text-xs">
                            {getInitials(feature.assignee.name)}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div />
                      )}

                      {taskTotal > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-emerald-500/70 transition-all"
                              style={{ width: `${String(progress)}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {taskDone}/{taskTotal}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                );

                if (prefersReduced) {
                  return (
                    <div
                      key={item.data.id}
                      className={cn(
                        "glass-card rounded-lg p-3 cursor-pointer transition-all hover:bg-white/[0.04]",
                        isSelected && "bg-white/[0.06]"
                      )}
                      onClick={() => onItemSelect?.(item)}
                    >
                      {cardContent}
                    </div>
                  );
                }

                return (
                  <motion.div
                    key={item.data.id}
                    layoutId={item.data.id}
                    layout
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: "easeOut", layout: { duration: 0.25 } }}
                    className={cn(
                      "glass-card rounded-lg p-3 cursor-pointer transition-[background-color] hover:bg-white/[0.04]",
                      isSelected && "bg-white/[0.06]"
                    )}
                    onClick={() => onItemSelect?.(item)}
                  >
                    {cardContent}
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {col.items.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-8">
                No items
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
